import type { GridApi, IRowNode } from 'ag-grid-community';

export const isAncestorOf = (ancestor: IRowNode, descendant: IRowNode): boolean => {
  let current = descendant.parent;
  while (current) {
    if (current === ancestor) return true;
    current = current.parent;
  }
  return false;
};

// Checks if a row can be expanded: excludes placeholders (loading indicators) and leaf rows.
// When potentialParent is given, scopes to descendants of that row only.
export const isEligibleNode = (node: IRowNode, potentialParent?: IRowNode): boolean => {
  const data = node.data as { placeholder?: boolean; canExpand?: boolean } | undefined;
  if (data?.placeholder || !data?.canExpand) return false;
  return !potentialParent || isAncestorOf(potentialParent, node);
};

// Loaded rows with the least expansion depth below `node` (or across the report), and their level.
export function collectNodesToExpandOneLevel(api: GridApi, node?: IRowNode): { level: number; nodes: Set<IRowNode> } {
  const nodesToExpand = new Set<IRowNode>();
  let levelToExpand = Number.POSITIVE_INFINITY;

  api.forEachNode((currNode) => {
    // If it is already an empty node there is no point to expand it
    const isCollapsedCandidate = Boolean(currNode.key) && !currNode.expanded && isEligibleNode(currNode, node);
    if (!isCollapsedCandidate || currNode.level > levelToExpand) return;
    // If we've found a node to expand at level closer to top
    if (currNode.level < levelToExpand) {
      nodesToExpand.clear();
    }
    levelToExpand = currNode.level;
    nodesToExpand.add(currNode);
  });

  return { level: levelToExpand, nodes: nodesToExpand };
}

// Shallowest level below `node` (or across the report) of rows still waiting for server data.
function getShallowestLoadingLevel(api: GridApi, node?: IRowNode): number | undefined {
  let shallowestLevel: number | undefined;
  api.forEachNode((currNode) => {
    if (currNode.data) return;
    if (node && !isAncestorOf(node, currNode)) return;
    if (shallowestLevel === undefined || currNode.level < shallowestLevel) {
      shallowestLevel = currNode.level;
    }
  });
  return shallowestLevel;
}

/**
 * The row level "Expand one level" will open for this payload, or undefined when it only toggles
 * the given row or finds nothing to open. Rows below the viewport are not loaded yet, so the caller
 * keeps opening rows at this level as they load. When the next level has not loaded at all yet
 * (the previous "Expand one level" is still fetching its children), the level of the rows still
 * loading is the one to open as they land.
 */
export function getExpandOneLevelTargetLevel(api: GridApi, node?: IRowNode): number | undefined {
  if (node && !node.expanded) return undefined;
  const { level, nodes } = collectNodesToExpandOneLevel(api, node);
  if (nodes.size > 0) return level;
  return getShallowestLoadingLevel(api, node);
}
